import { useState, useRef, useEffect, useCallback , type SyntheticEvent, type KeyboardEvent as ReactKeyboardEvent, type ChangeEvent as ReactChangeEvent, type ClipboardEvent as ReactClipboardEvent, type DragEvent as ReactDragEvent, type SyntheticEvent as ReactSyntheticEvent } from 'react'
import { useAuth } from '../context/AuthContext'
import {
  SendIcon, LoaderIcon, PaperclipIcon, ImageIcon, FileIcon, XIcon,
  ReplyIcon, MicIcon, AudioIcon, SpellCheckIcon, GifIcon,
} from '../icons'
import { EmojiPicker } from './EmojiPicker'
import { GifPicker } from './GifPicker'
import { avatarUrl } from '../utils/avatar'
import { attachmentIcon } from '../utils/attachments'
import { useWikiPages } from '../hooks/useWikiPages'
import type { Attachment, PublicUser } from '../types'
import { API_URL, getAuthHeaders } from '../api'


function formatFileSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function formatRecordTime(secs: number) {
  const m = Math.floor(secs / 60)
  const s = secs % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

/**
 * Shared chat input bar used by DMView and GroupChannelView.
 *
 * Props:
 *   onSend(text, file, replyTo)  — called when user sends a message
 *   onSendAudio(file)            — called when user sends an audio recording
 *   replyTo                      — message object being replied to (or null)
 *   onCancelReply                — callback to clear reply
 *   replyLabel                   — string label for reply preview (e.g. "Respondendo a Mateus")
 *   placeholder                  — textarea placeholder
 *   disabled                     — whether sending is in progress (shows loader)
 *   autoFocus                    — whether to focus input on mount/change
 */
export function ChatInputBar({
  onSend,
  onSendAudio,
  replyTo,
  onCancelReply,
  replyLabel,
  placeholder,
  disabled,
  autoFocusOnReply = true,
  mentionUsers = [] as PublicUser[],
}: {
  onSend: (text: string, files: File[], replyTo: unknown) => void
  onSendAudio?: (file: File) => void
  replyTo?: { id?: string; content?: string | null; sender?: unknown; attachment_name?: string | null } | null
  onCancelReply?: () => void
  replyLabel?: string
  placeholder?: string
  disabled?: boolean
  autoFocusOnReply?: boolean
  mentionUsers?: Array<Partial<PublicUser> & { user_id?: string; display_name?: string; username?: string }>
}) {
  const { user } = useAuth()
  const [input, setInput] = useState('')
  const [attachments, setAttachments] = useState<Attachment[]>([])
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const [emojiPickerPos, setEmojiPickerPos] = useState<{ x: number; y: number } | null>(null)
  const [showGifPicker, setShowGifPicker] = useState(false)
  const [spellCheckEnabled, setSpellCheckEnabled] = useState(
    () => localStorage.getItem('atlas_spellcheck') === 'true'
  )
  const [spellChecking, setSpellChecking] = useState(false)
  const [isRecording, setIsRecording] = useState(false)
  const [recordTime, setRecordTime] = useState(0)
  const [isDragOver, setIsDragOver] = useState(false)

  // Mention state
  const [mentionQuery, setMentionQuery] = useState<string | null>(null) // null = not active, string = filtering
  const [mentionIndex, setMentionIndex] = useState(0)
  const [mentionStart, setMentionStart] = useState(-1) // cursor position where @ was typed

  // Wiki #reference state
  const { wikiPages } = useWikiPages()
  const [wikiQuery, setWikiQuery] = useState<string | null>(null)
  const [wikiIndex, setWikiIndex] = useState(0)
  const [wikiStart, setWikiStart] = useState(-1)

  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const inputRef = useRef<HTMLTextAreaElement | null>(null)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const audioChunksRef = useRef<Blob[]>([])
  const recordTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const streamRef = useRef<MediaStream | null>(null)

  const authHeaders = getAuthHeaders()

  const busy = disabled || spellChecking

  // Focus input when replyTo changes
  useEffect(() => {
    if (replyTo && autoFocusOnReply) inputRef.current?.focus()
  }, [replyTo, autoFocusOnReply])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recordTimerRef.current) clearInterval(recordTimerRef.current)
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop())
    }
  }, [])

  // --- Spell check ---
  const runSpellCheck = async (text: string) => {
    if (!spellCheckEnabled || !text) return text
    setSpellChecking(true)
    try {
      const controller = new AbortController()
      const timeout = setTimeout(() => controller.abort(), 10000)
      const res = await fetch(`${API_URL}/api/spellcheck`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ text }),
        signal: controller.signal,
      })
      clearTimeout(timeout)
      if (res.ok) {
        const data = await res.json()
        if (data.corrected && data.corrected !== text) return data.corrected
      }
    } catch { /* timeout or error — send original */ }
    setSpellChecking(false)
    return text
  }

  // --- Send ---
  const handleSend = async (e?: SyntheticEvent | null) => {
    e?.preventDefault()
    let text = input.trim()
    if ((!text && attachments.length === 0) || disabled || spellChecking) return

    // Spell check before sending
    if (spellCheckEnabled && text) {
      setSpellChecking(true)
      try {
        const controller = new AbortController()
        const timeout = setTimeout(() => controller.abort(), 10000)
        const res = await fetch(`${API_URL}/api/spellcheck`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders },
          body: JSON.stringify({ text }),
          signal: controller.signal,
        })
        clearTimeout(timeout)
        if (res.ok) {
          const data = await res.json()
          if (data.corrected && data.corrected !== text) text = data.corrected
        }
      } catch { /* timeout or error — send original */ }
      setSpellChecking(false)
    }

    const currentAttachments = attachments
    const currentReplyTo = replyTo

    setInput('')
    currentAttachments.forEach(a => { if (a.preview) URL.revokeObjectURL(a.preview) })
    setAttachments([])
    if (fileInputRef.current) fileInputRef.current.value = ''

    await onSend(text, currentAttachments.map(a => a.file!), currentReplyTo)
    requestAnimationFrame(() => inputRef.current?.focus())
  }

  const handleKeyDown = (e: ReactKeyboardEvent) => {
    // Mention navigation
    if (mentionQuery !== null) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setMentionIndex(prev => Math.min(prev + 1, filteredMentions.length - 1))
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setMentionIndex(prev => Math.max(prev - 1, 0))
        return
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        if (filteredMentions[mentionIndex]) {
          insertMention(filteredMentions[mentionIndex])
        }
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        setMentionQuery(null)
        setMentionStart(-1)
        return
      }
    }

    // Wiki #reference navigation
    if (wikiQuery !== null) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setWikiIndex(prev => Math.min(prev + 1, filteredWikiPages.length - 1))
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setWikiIndex(prev => Math.max(prev - 1, 0))
        return
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        if (filteredWikiPages[wikiIndex]) {
          insertWikiRef(filteredWikiPages[wikiIndex])
        }
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        setWikiQuery(null)
        setWikiStart(-1)
        return
      }
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      if (!spellChecking) handleSend()
    }
  }

  // --- Mentions ---
  // Build the list of mentionable entries: "all", "atlas", then users
  const mentionableEntries = [
    { id: 'all', name: 'all', display_name: 'Todos', avatar_url: null, isSpecial: true },
    { id: 'atlas', name: 'atlas', display_name: 'Atlas', avatar_url: null, isSpecial: true },
    ...mentionUsers
      .filter((u) => (u.user_id || u.id) !== user?.id) // don't mention yourself
      .map((u) => ({
        id: u.user_id || u.id,
        name: u.name || u.email || '',
        display_name: u.display_name || u.name || u.email || '',
        avatar_url: u.avatar_url || null,
        isSpecial: false,
      })),
  ]

  const filteredMentions = mentionQuery !== null
    ? mentionableEntries.filter(u => {
        const q = mentionQuery.toLowerCase()
        return (u.name || '').toLowerCase().includes(q) ||
               (u.display_name || '').toLowerCase().includes(q)
      }).slice(0, 8)
    : []

  const insertMention = (entry: { id?: string; name?: string; display_name?: string }) => {
    const before = input.slice(0, mentionStart)
    const after = input.slice(inputRef.current?.selectionStart || input.length)
    const mention = `@${entry.name || entry.display_name} `
    const newVal = before + mention + after
    setInput(newVal)
    setMentionQuery(null)
    setMentionStart(-1)
    requestAnimationFrame(() => {
      const pos = (before + mention).length
      inputRef.current?.focus()
      inputRef.current?.setSelectionRange(pos, pos)
    })
  }

  // --- Wiki #reference ---
  const filteredWikiPages = wikiQuery !== null
    ? wikiPages.filter(p => {
        const q = wikiQuery.toLowerCase()
        return (p.title || '').toLowerCase().includes(q)
      }).slice(0, 8)
    : []

  const insertWikiRef = (page: { id: string; title: string }) => {
    const before = input.slice(0, wikiStart)
    const after = input.slice(inputRef.current?.selectionStart || input.length)
    const origin = window.location.origin
    const ref = `[#${page.title}](${origin}/wiki?page=${page.id}) `
    const newVal = before + ref + after
    setInput(newVal)
    setWikiQuery(null)
    setWikiStart(-1)
    requestAnimationFrame(() => {
      const pos = (before + ref).length
      inputRef.current?.focus()
      inputRef.current?.setSelectionRange(pos, pos)
    })
  }

  const handleInputChange = (e: ReactChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const val = e.target.value
    const cursor = (e.target.selectionStart ?? 0)
    setInput(val)

    // Detect @mention: look backwards from cursor for @ that's at start or after whitespace
    if (mentionUsers.length > 0 || true) { // always allow all/atlas
      const beforeCursor = val.slice(0, cursor)
      const atIdx = beforeCursor.lastIndexOf('@')
      if (atIdx >= 0 && (atIdx === 0 || /\s/.test(val[atIdx - 1]))) {
        const query = beforeCursor.slice(atIdx + 1)
        // Only activate if query has no spaces (still typing the name)
        // and only contains word characters / hyphens (not dots like emails)
        if (!query.includes(' ') && query.length <= 30 && /^[\w-]*$/.test(query)) {
          // Check that what follows the query (at cursor) is a space, end of string, or punctuation
          // This prevents @person.com from triggering (no space after "person")
          const afterQuery = val.slice(cursor)
          if (afterQuery.length === 0 || /^\s/.test(afterQuery) || /^[.,;:!?\n]/.test(afterQuery)) {
            setMentionQuery(query)
            setMentionStart(atIdx)
            setMentionIndex(0)
            return
          }
        }
      }
    }
    if (mentionQuery !== null) {
      setMentionQuery(null)
      setMentionStart(-1)
    }

    // Detect #wiki reference: look backwards from cursor for # at start or after whitespace
    {
      const beforeCursor = val.slice(0, cursor)
      const hashIdx = beforeCursor.lastIndexOf('#')
      if (hashIdx >= 0 && (hashIdx === 0 || /\s/.test(val[hashIdx - 1]))) {
        const query = beforeCursor.slice(hashIdx + 1)
        if (!query.includes(' ') && query.length <= 50 && /^[\w-]*$/.test(query)) {
          const afterQuery = val.slice(cursor)
          if (afterQuery.length === 0 || /^\s/.test(afterQuery) || /^[.,;:!?\n]/.test(afterQuery)) {
            setWikiQuery(query)
            setWikiStart(hashIdx)
            setWikiIndex(0)
            return
          }
        }
      }
    }
    if (wikiQuery !== null) {
      setWikiQuery(null)
      setWikiStart(-1)
    }
  }

  // --- File handling ---
  const handleFileSelect = (e: ReactChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    if (files.length === 0) return
    files.forEach(f => addFile(f))
    e.target.value = ''
  }

  const addFile = useCallback((file: File) => {
    if (!file) return
    setAttachments(prev => [...prev, {
      file,
      name: file.name || `pasted-${Date.now()}.png`,
      type: file.type,
      size: file.size,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
    }])
  }, [])

  const removeAttachment = (index: number) => {
    setAttachments(prev => {
      const item = prev[index]
      if (item?.preview) URL.revokeObjectURL(item.preview)
      return prev.filter((_, i) => i !== index)
    })
  }

  const handlePaste = (e: ReactClipboardEvent) => {
    const items = e.clipboardData?.items
    if (!items) return
    for (let i = 0; i < items.length; i++) {
      if (items[i].kind === 'file') {
        const file = items[i].getAsFile()
        if (file) {
          e.preventDefault()
          if (!file.name || file.name === 'image.png') {
            const ext = file.type.split('/')[1] || 'png'
            const named = new File([file], `pasted-${Date.now()}.${ext}`, { type: file.type })
            addFile(named)
          } else {
            addFile(file)
          }
          break
        }
      }
    }
  }

  // --- Drag and drop file handling ---
  const dragCounterRef = useRef(0)

  const handleDragEnter = (e: ReactDragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.dataTransfer?.types?.includes('Files')) {
      dragCounterRef.current++
      setIsDragOver(true)
    }
  }

  const handleDragLeave = (e: ReactSyntheticEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounterRef.current--
    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0
      setIsDragOver(false)
    }
  }

  const handleDragOver = (e: ReactDragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.dataTransfer?.types?.includes('Files')) {
      e.dataTransfer.dropEffect = 'copy'
    }
  }

  const handleDrop = (e: ReactDragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounterRef.current = 0
    setIsDragOver(false)
    const files = e.dataTransfer?.files
    if (files && files.length > 0) {
      const file = files[0]
      if (file.type.startsWith('image/')) {
        addFile(file)
      } else {
        // Also accept non-image files
        addFile(file)
      }
    }
  }

  // --- Audio recording ---
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream
      const recorder = new MediaRecorder(stream)
      mediaRecorderRef.current = recorder
      audioChunksRef.current = []

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data)
      }

      recorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' })
        const file = new File([blob], `audio-${Date.now()}.webm`, { type: 'audio/webm' })
        streamRef.current?.getTracks().forEach(t => t.stop())
        streamRef.current = null
        if (onSendAudio) onSendAudio(file)
      }

      recorder.start()
      setIsRecording(true)
      setRecordTime(0)
      recordTimerRef.current = setInterval(() => {
        setRecordTime(prev => prev + 1)
      }, 1000)
    } catch { /* mic permission denied */ }
  }

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop()
    }
    setIsRecording(false)
    if (recordTimerRef.current) {
      clearInterval(recordTimerRef.current)
      recordTimerRef.current = null
    }
  }

  const cancelRecording = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.onstop = null
      mediaRecorderRef.current.stop()
    }
    setIsRecording(false)
    if (recordTimerRef.current) {
      clearInterval(recordTimerRef.current)
      recordTimerRef.current = null
    }
    streamRef.current?.getTracks().forEach(t => t.stop())
    streamRef.current = null
  }

  // --- Render ---
  return (
    <div
      className="chat-input-container"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {isDragOver && (
        <div className="chat-drag-overlay">
          <div className="chat-drag-overlay-content">
            <ImageIcon size={48} />
            <span>Solte a imagem aqui para enviar</span>
          </div>
        </div>
      )}
      {/* Reply preview */}
      {replyTo && (
        <div className="dm-reply-preview">
          <ReplyIcon size={14} />
          <div className="dm-reply-preview-content">
            <span className="dm-reply-preview-label">
              {replyLabel || 'Respondendo'}
            </span>
            <span className="dm-reply-preview-text">
              {replyTo.content || (replyTo.attachment_name ? `[${replyTo.attachment_name}]` : '...')}
            </span>
          </div>
          <button className="dm-reply-cancel" onClick={onCancelReply}>
            <XIcon size={14} />
          </button>
        </div>
      )}

      {/* Attachments preview */}
      {attachments.length > 0 && (
        <div className="dm-attachment-preview-list">
          {attachments.map((att, i) => (
            <div key={i} className="dm-attachment-preview">
              {att.preview ? (
                <img src={att.preview} alt="" className="dm-attachment-preview-img" />
              ) : (
                <div className="dm-attachment-preview-icon">{attachmentIcon(att.type)}</div>
              )}
              <div className="dm-attachment-preview-info">
                <span className="dm-attachment-preview-name">{att.name}</span>
                <span className="dm-attachment-preview-size">{formatFileSize(att.size ?? 0)}</span>
              </div>
              <button className="dm-attachment-preview-remove" onClick={() => removeAttachment(i)}>
                <XIcon size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {isRecording ? (
        <div className="dm-recording-bar">
          <span className="dm-record-pulse" />
          <span className="dm-record-time">{formatRecordTime(recordTime)}</span>
          <span className="dm-record-label">Gravando áudio...</span>
          <button type="button" className="dm-record-cancel" onClick={cancelRecording} title="Cancelar">
            <XIcon size={18} />
          </button>
          <button type="button" className="dm-record-send" onClick={stopRecording} title="Enviar áudio">
            <SendIcon size={18} />
          </button>
        </div>
      ) : (
        <form className="hermes-chat-input-row" onSubmit={handleSend}>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileSelect}
            multiple
            accept="image/*,audio/*,video/*,.pdf,.txt,.md,.json,.csv,.xml,.yaml,.yml,.zip,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
          />
          <button
            type="button"
            className="dm-attach-btn"
            onClick={() => fileInputRef.current?.click()}
            disabled={busy}
            title="Anexar arquivo"
          >
            <PaperclipIcon size={18} />
          </button>
          <button
            type="button"
            className={`dm-spellcheck-btn ${spellCheckEnabled ? 'active' : ''}`}
            onClick={() => {
              const next = !spellCheckEnabled
              setSpellCheckEnabled(next)
              localStorage.setItem('atlas_spellcheck', String(next))
            }}
            disabled={busy}
            title={spellCheckEnabled ? 'Revisão ortográfica ativada' : 'Ativar revisão ortográfica'}
          >
            {spellChecking ? <LoaderIcon size={16} className="spin" /> : <SpellCheckIcon size={16} />}
          </button>
          <div className="dm-input-wrapper">
            {showEmojiPicker && (
              <EmojiPicker
                x={emojiPickerPos?.x}
                y={emojiPickerPos?.y}
                onSelect={(emoji) => {
                  setInput(prev => prev + emoji)
                  setShowEmojiPicker(false)
                }}
                onClose={() => setShowEmojiPicker(false)}
              />
            )}
            {showGifPicker && (
              <GifPicker
                onSelect={(file) => {
                  addFile(file)
                  setShowGifPicker(false)
                }}
                onClose={() => setShowGifPicker(false)}
              />
            )}
            {mentionQuery !== null && filteredMentions.length > 0 && (
              <div className="mention-dropdown">
                {filteredMentions.map((entry, i) => (
                  <button
                    key={entry.id}
                    type="button"
                    className={`mention-item ${i === mentionIndex ? 'active' : ''}`}
                    onMouseDown={(e) => {
                      e.preventDefault()
                      insertMention(entry)
                    }}
                    onMouseEnter={() => setMentionIndex(i)}
                  >
                    {entry.avatar_url ? (
                      <img src={avatarUrl({ avatar_url: entry.avatar_url })} alt="" className="mention-avatar" />
                    ) : (
                      <div className="mention-avatar mention-avatar-placeholder">
                        {entry.isSpecial ? '@' : (entry.display_name?.[0] || '?').toUpperCase()}
                      </div>
                    )}
                    <span className="mention-name">{entry.display_name}</span>
                    {entry.name && entry.name !== entry.display_name && (
                      <span className="mention-username">@{entry.name}</span>
                    )}
                  </button>
                ))}
              </div>
            )}
            {wikiQuery !== null && filteredWikiPages.length > 0 && (
              <div className="mention-dropdown">
                {filteredWikiPages.map((page, i) => (
                  <button
                    key={page.id}
                    type="button"
                    className={`mention-item ${i === wikiIndex ? 'active' : ''}`}
                    onMouseDown={(e) => {
                      e.preventDefault()
                      insertWikiRef(page)
                    }}
                    onMouseEnter={() => setWikiIndex(i)}
                  >
                    <div className="mention-avatar mention-avatar-placeholder" style={{ background: 'var(--accent, #2563eb)' }}>
                      #
                    </div>
                    <span className="mention-name">{page.title}</span>
                  </button>
                ))}
              </div>
            )}
            <textarea
              ref={inputRef}
              className="hermes-chat-input"
              placeholder={spellCheckEnabled
                ? `${placeholder || 'Mensagem...'} (revisão automática ativa)`
                : (placeholder || 'Mensagem... (Enter para enviar)')}
              value={input}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              rows={1}
            />
            <button
              type="button"
              className="dm-emoji-btn-inside"
              onClick={(e) => {
                setEmojiPickerPos({ x: e.clientX, y: e.clientY })
                setShowEmojiPicker(!showEmojiPicker)
                setShowGifPicker(false)
              }}
              title="Emoji"
            >
              <span style={{ fontSize: 18 }}>😊</span>
            </button>
            <button
              type="button"
              className="dm-emoji-btn-inside dm-gif-btn-inside"
              onClick={() => {
                setShowGifPicker(!showGifPicker)
                setShowEmojiPicker(false)
              }}
              disabled={busy}
              title="GIF"
            >
              <GifIcon size={18} />
            </button>
          </div>
          <button
            type="button"
            className="dm-mic-btn"
            onClick={startRecording}
            disabled={busy}
            title="Gravar áudio"
          >
            <MicIcon size={18} />
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={busy || (!input.trim() && attachments.length === 0)}
          >
            {disabled || spellChecking ? <LoaderIcon size={16} className="spin" /> : <SendIcon size={16} />}
          </button>
        </form>
      )}
    </div>
  )
}
